import TableLoadingView from '@/components/tables/TableLoadingView';
import { AlertCircle, FolderOpen, SearchX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { DocumentFile, DocumentFolder } from '../../types';
import type { DocumentPermission } from '../../types/document';
import { DocumentEmptyState } from '../partials/DocumentEmptyState';
import { DocumentList } from './DocumentList';
import type { DragItemData } from './useDragAndDrop';
import type { SelectableItem } from './useMultiselect';
import { FileAction, FolderAction } from './DocumentList.types';

interface DocumentExplorerContentProps {
  isNotFoundError: boolean;
  isInitialLoading: boolean;
  isError: boolean;
  isRefreshingList: boolean;
  hasItems: boolean;
  isAdmin: boolean;
  trimmedSearch: string;
  folders: DocumentFolder[];
  files: DocumentFile[];
  permission: DocumentPermission;
  deviceModelNames: Map<number, string>;
  editingFolderId: string | null;
  editingFolderName: string;
  isRenamingFolder: boolean;
  isFetchingNextPage: boolean;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
  onFolderClick: (folder: DocumentFolder) => void;
  onFileClick: (file: DocumentFile) => void;
  onFolderAction: (actionLabel: FolderAction, folder: DocumentFolder) => void;
  onFolderRenameStart: (folder: DocumentFolder) => void;
  onFolderRenameChange: (name: string) => void;
  onFolderRenameSubmit: (folder: DocumentFolder) => void;
  onFolderRenameCancel: () => void;
  onFileAction: (actionLabel: FileAction, file: DocumentFile) => void;
  onRetry: () => void;
  // DnD
  isDndActive?: boolean;
  draggedItem?: DragItemData | null;
  // Multiselect
  isSelected?: (id: string) => boolean;
  onToggleSelect?: (item: SelectableItem) => void;
  selectedCount?: number;
  isBulkMoving?: boolean;
  isBulkDeleting?: boolean;
  onBulkMoveClick?: () => void;
  onBulkDeleteClick?: () => void;
}

/**
 * Renders the main content area of the document explorer.
 * Switches between loading skeleton, error state, empty state, and the document list.
 */
export function DocumentExplorerContent({
  isNotFoundError,
  isInitialLoading,
  isError,
  isRefreshingList,
  hasItems,
  isAdmin,
  trimmedSearch,
  folders,
  files,
  permission,
  deviceModelNames,
  editingFolderId,
  editingFolderName,
  isRenamingFolder,
  isFetchingNextPage,
  sentinelRef,
  onFolderClick,
  onFileClick,
  onFolderAction,
  onFolderRenameStart,
  onFolderRenameChange,
  onFolderRenameSubmit,
  onFolderRenameCancel,
  onFileAction,
  onRetry,
  isDndActive,
  draggedItem,
  isSelected,
  onToggleSelect,
  selectedCount,
  isBulkMoving,
  isBulkDeleting,
  onBulkMoveClick,
  onBulkDeleteClick,
}: DocumentExplorerContentProps) {
  const { t } = useTranslation();

  if (isNotFoundError) {
    return (
      <DocumentEmptyState
        icon={<AlertCircle className="size-8" />}
        title={t('documents.states.folderNotFoundTitle')}
        description={t('documents.states.folderNotFoundDescription')}
      />
    );
  }

  if (isInitialLoading) {
    return (
      <TableLoadingView
        isLoading={true}
        rows={6}
        columns={isAdmin ? 7 : 6}
        withHeader={true}
      />
    );
  }

  if (isError) {
    return (
      <DocumentEmptyState
        icon={<AlertCircle className="size-8" />}
        title={t('documents.states.errorTitle')}
        description={t('documents.states.errorDescription')}
        actionLabel={t('documents.actions.retry')}
        onAction={onRetry}
      />
    );
  }

  if (!hasItems) {
    const emptyStateIcon = trimmedSearch ? <SearchX className="size-8" /> : <FolderOpen className="size-8" />;
    const emptyStateTitle = trimmedSearch ? t('documents.states.noSearchResultsTitle') : t('documents.states.emptyTitle');
    const emptyStateDescription = trimmedSearch
      ? t('documents.states.noSearchResultsDescription')
      : t('documents.states.emptyDescription');

    return (
      <DocumentEmptyState
        icon={emptyStateIcon}
        title={emptyStateTitle}
        description={emptyStateDescription}
      />
    );
  }

  return (
    <div className="relative h-full grow flex flex-col overflow-hidden">
      {isRefreshingList ? (
        <div className="absolute top-4 right-4 z-10">
          <div className="border-primary size-5 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : null}

      <DocumentList
        folders={folders}
        files={files}
        permission={permission}
        deviceModelNames={deviceModelNames}
        onFolderClick={onFolderClick}
        onFileClick={onFileClick}
        onFolderAction={onFolderAction}
        editingFolderId={editingFolderId}
        editingFolderName={editingFolderName}
        isRenamingFolder={isRenamingFolder}
        onFolderRenameStart={onFolderRenameStart}
        onFolderRenameChange={onFolderRenameChange}
        onFolderRenameSubmit={onFolderRenameSubmit}
        onFolderRenameCancel={onFolderRenameCancel}
        onFileAction={onFileAction}
        sentinelRef={sentinelRef}
        isFetchingNextPage={isFetchingNextPage}
        isDndActive={isDndActive}
        draggedItem={draggedItem}
        isSelected={isSelected}
        onToggleSelect={onToggleSelect}
        selectedCount={selectedCount}
        isBulkMoving={isBulkMoving}
        isBulkDeleting={isBulkDeleting}
        onBulkMoveClick={onBulkMoveClick}
        onBulkDeleteClick={onBulkDeleteClick}
      />
    </div>
  );
}
